import {useTranslation} from "react-i18next";
import {useHasScope} from "../../../../config/globalConfig";
import NotAuthorizedDialog from "../../../authentication/component/NotAuthorizedDialog";
import {SCOPE_FINANCE_REFUELLING_EDIT, SCOPE_FINANCE_REFUELLING_VIEW,} from "../../utils/FinanceScope";
import {Button, Card, CardActions, CardContent} from "@mui/material";
import SectionHeader from "../../../../util/component/SectionHeader";
import {IRefuellingData, useFuelCardsRepository,} from "../../api/repositories/RefuellingRepository";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import PriceBox from "./components/PriceBox";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {setSnackbar} from "../../../../util/state/globalAction";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {formatDate} from "../../utils/FormatUtils";
import {useRecoilState, useRecoilValue} from "recoil";
import {STATE_REFUELLING_ACTIVE_FILTERS} from "./constants/ConstantsRefuelling";
import RefuellingFilters from "./filters/RefuellingFilters";

function Refuelling() {
    const {dispatchGlobalData} = useContext(GlobalContext);

    const [data, setData] = useState<IRefuellingData>();
    const [loading, setLoading] = useState<boolean>(false);
    const [reload, setReload] = useState<boolean>(false);

    const {loadRefuellingData, saveRefuellingData} = useFuelCardsRepository();
    const [filterDefs, setFilterDefs] = useRecoilState(STATE_REFUELLING_ACTIVE_FILTERS);
    const [hideFields, setHideFields] = useState<boolean>(false);

    useEffect(() => {
        setLoading(true);
        setReload(false);
        loadRefuellingData(filterDefs).then((data: any) => {
            [
                "priceKwh",
                "basePriceGas",
                "basePriceDiesel",
                "priceIncreaseGas",
                "priceIncreaseDiesel",
            ].forEach(
                (k) => (data[k] = Math.abs(data[k]) > 0 ? data[k] / 1000 : data[k])
            );

            ["percentageIncreaseGas", "percentageIncreaseDiesel"].forEach(
                (k) => (data[k] = Number(data[k]))
            );

            setData(data);
            setLoading(false);
        });
        setHideFields(filterDefs[0]?.value && filterDefs[0].value !== "drivalia_it");
    }, [reload, filterDefs]);

    const {t} = useTranslation("finance", {keyPrefix: "refuelling"});
    const hasScope = useHasScope;
    const editable = hasScope(SCOPE_FINANCE_REFUELLING_EDIT);

    if (!hasScope(SCOPE_FINANCE_REFUELLING_VIEW)) return <NotAuthorizedDialog/>;

    const saveData = () => {
        saveRefuellingData(data!)
            .then((response) => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t("api.success-update"),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true,
                        },
                    },
                });
            })
            .catch(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t("api.error-update"),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        },
                    },
                });
            });
    };

    const countDecimals = (value: number) => {
        if ((value % 1) !== 0 && value !== undefined) {
            return value.toString().split(".")[1]?.length ?? 0;
        }
        return 0;
    };
    const handleChange = (name: string) => (event: any) => {
        const value = parseFloat(event.target.value);
        const fixedDigitsValue = countDecimals(value) > 2 ? Math.min(Number(value.toFixed(2)), 99.99) : value;
        const effectiveValue = event.target.value.length === 0 ? event.target.value : fixedDigitsValue;
        if ((effectiveValue >= 0 && effectiveValue < 100) || event.target.value.length === 0) {
            if(hideFields) {
                setData({
                    ...data,
                    [name]: effectiveValue.toString(),
                    priceIncreaseGas: name === "percentageIncreaseGas" ? effectiveValue : data?.priceIncreaseGas,
                    priceIncreaseDiesel: name === "percentageIncreaseDiesel" ? effectiveValue : data?.priceIncreaseDiesel,
                    percentageIncreaseGas: null,
                    percentageIncreaseDiesel: null,
                } as IRefuellingData);
            } else {
                setData({
                    ...data,
                    [name]: effectiveValue.toString(),
                    priceIncreaseGas:
                        name === "percentageIncreaseGas" && data?.basePriceGas
                            ? (
                                data.basePriceGas +
                                data.basePriceGas * (effectiveValue || 0) * 0.01
                            ).toFixed(3)
                            : data?.priceIncreaseGas,
                    priceIncreaseDiesel:
                        name === "percentageIncreaseDiesel" && data?.basePriceDiesel
                            ? (
                                data.basePriceDiesel +
                                data.basePriceDiesel * (effectiveValue || 0) * 0.01
                            ).toFixed(3)
                            : data?.priceIncreaseDiesel,
                } as IRefuellingData);
            }
        }
    };
    return (
        <>
            <SectionHeader
                title={t("header.title")}
                subtitle={t("header.subtitle", {
                    date: data ? formatDate(new Date(data.detectionDate)) : "---",
                })}
            />


            <Card variant="outlined" sx={{margin: "15px"}}>
                <CardContent>
                    <RefuellingFilters/>
                    <PriceBox
                        title={t("gas-description")}
                        base={data?.basePriceGas}
                        disabled={loading}
                        percentage={data?.percentageIncreaseGas}
                        total={data?.priceIncreaseGas}
                        hide={hideFields}
                        onChange={handleChange("percentageIncreaseGas")}
                    />
                    <PriceBox
                        title={t("diesel-description")}
                        disabled={loading}
                        base={data?.basePriceDiesel}
                        percentage={data?.percentageIncreaseDiesel}
                        total={data?.priceIncreaseDiesel}
                        hide={hideFields}
                        onChange={handleChange("percentageIncreaseDiesel")}
                    />

                    <PriceBox
                        title={t("kwh-description")}
                        disabled={loading}
                        base={data?.priceKwh}
                        hide={false}
                        onChange={handleChange("priceKwh")}
                        editPrice={true}
                    />
                </CardContent>
                <CardActions sx={{justifyContent: "flex-end"}}>
                    <Button
                        onClick={() => setReload(true)}
                        disabled={loading}
                        size="small"
                        color={"accent"}
                        variant={"outlined"}
                    >
                        {t("actions.reload")}
                    </Button>
                    <Button
                        onClick={saveData}
                        disabled={!editable || loading || !data}
                        size="small"
                    >
                        {t("actions.save")}
                    </Button>
                </CardActions>
            </Card>
        </>
    );
}

export default Refuelling;
